import { useEffect, useState } from "react";
import { api, BACKEND_URL, tokenStore } from "../lib/api";
import { Card } from "./ui/card";
import { ClipboardCheck, LogIn, LogOut, ShieldCheck } from "lucide-react";

/**
 * Read-only viewer for boat-inspection photos uploaded by the customer.
 * Shows check-in and check-out photos side-by-side so staff can compare.
 */
export default function AdminBoatInspectionCard({ bookingId }) {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    if (!bookingId) return;
    setLoading(true);
    try {
      const r = await api.get(`/customer/boat-inspection/${bookingId}`);
      setPhotos(r.data || []);
    } catch {
      setPhotos([]);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [bookingId]);

  const raw = (id) =>
    `${BACKEND_URL}/api/boat-inspection/photo/${id}/raw?auth=${encodeURIComponent(tokenStore.get())}`;
  const fmt = (iso) => {
    if (!iso) return "";
    try {
      return new Date(iso).toLocaleString("nb-NO", {
        year: "numeric", month: "2-digit", day: "2-digit",
        hour: "2-digit", minute: "2-digit",
      });
    } catch { return iso; }
  };

  const groups = {
    checkin: photos.filter((p) => p.kind === "checkin"),
    checkout: photos.filter((p) => p.kind === "checkout"),
  };

  if (!loading && photos.length === 0) return null;

  const renderSection = (kind) => {
    const list = groups[kind];
    if (!list || list.length === 0) return null;
    const Icon = kind === "checkin" ? LogIn : LogOut;
    return (
      <div key={kind}>
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#A57F39] font-semibold mb-2">
          <Icon className="w-4 h-4" />
          {kind === "checkin" ? "Innsjekk" : "Utsjekk"}
          <span className="text-[10px] text-slate-500 normal-case">({list.length})</span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {list.map((p) => (
            <a
              key={p.id}
              href={raw(p.id)}
              target="_blank"
              rel="noreferrer"
              data-testid={`admin-inspection-photo-${p.id}`}
              className="relative rounded-lg overflow-hidden border border-slate-200 bg-slate-50 aspect-square group"
            >
              <img src={raw(p.id)} alt="" className="w-full h-full object-cover" loading="lazy" />
              <div className="absolute bottom-0 left-0 right-0 bg-black/70 text-white text-[10px] px-1.5 py-1 flex items-center gap-1">
                <ClipboardCheck className="w-3 h-3 text-[#ECC974]" />
                <span className="truncate">{fmt(p.taken_at)}</span>
              </div>
              {p.note && (
                <div className="absolute top-1.5 left-1.5 max-w-[80%] bg-[#0C1824]/75 text-white text-[10px] px-1.5 py-1 rounded line-clamp-2">
                  {p.note}
                </div>
              )}
            </a>
          ))}
        </div>
      </div>
    );
  };

  return (
    <Card className="p-6" data-testid="admin-boat-inspection-card">
      <div className="flex items-center gap-2 mb-3">
        <ShieldCheck className="w-5 h-5 text-[#A57F39]" />
        <h2 className="text-lg font-semibold text-[#0C1824]">
          Kundens tilstandsbilder
        </h2>
      </div>
      <p className="text-xs text-[#2B3F4D] mb-4">
        Bilder tatt av kunden ved inn- og utsjekk, med automatisk tidsstempel.
      </p>
      <div className="space-y-5">
        {renderSection("checkin")}
        {renderSection("checkout")}
      </div>
    </Card>
  );
}
